import * as THREE from 'three';
import { emis, smoke, std } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Cría de la Quemadura (docs/CRIATURAS.md §2, era III): a young of the dragon of the Burn, cast from the same
 * black volcanic glass. It flies over the field and burns a line across the ground. Kin of the dragon at a
 * glance: the parent's glass (shared materials), faceted plates along the flanks that catch the light, seams of
 * ember with the fire showing through, a small heart glowing in a rift of the chest ringed with blades, the
 * fan of glass at the tail's tip. But young: lean, long-limbed, the head and wings too big for the body, horns
 * still short, and where the parent wears its crown of fire only a ring of glass nubs over a faint glow.
 *
 * Not the ash wyvern: four legs drawn up under it and wings of their own off the shoulders; smooth membranes of
 * dark glass, translucent, with a red-violet sheen like the parent's smoke and a vein of ember in each finger.
 * Opaque shared materials, the ember its one self-lit material and the membrane its one translucent one, plus
 * glow sprites.
 *
 * It flies: the hover is its rest pose (`WYRMLING_HOVER`; the clips beat the wings and bob it, `die` drops it).
 * At rest the wings are spread flat, mid-beat. Bones: body; neck1–3 up to the head (jaw hinged under it);
 * `throat`, the fire in the throat on neck3 (the clips scale it: it swells before the breath); `breath`, a jet
 * of fire at the mouth on the head, built tiny so it is hidden until a clip scales it up (~×40 at full length);
 * tail1–5 down the whip; each wing in three (arm at the shoulder, fore at the elbow, hand at the wrist); four
 * legs (legF/legB thigh and shinF/shinB shin, L and R). The right wing is +X; on it +Z on `arm` lifts the wing,
 * +Y sweeps it back, −Y on `fore` and +Y on `hand` fold it (the left one mirrors). +X on body, neck and head
 * pitches them nose down; +X opens the jaw, lifts the tail and swings the legs back.
 */

/** Height of the model's origin over the ground while it flies (the clips drop it from here to die). */
export const WYRMLING_HOVER = 1.3;
/** How small the jet of fire is built (the clips scale `breath` by about 1 / BREATH_REST to show it). */
export const BREATH_REST = 0.025;
/** How much bigger than its build the head is (a young thing's head, too big for its body). */
const HEAD_K = 1.3;

/**
 * A membrane of glass from `hub` to the edge a→b: rings of quads bellying a little, the edge scalloped in toward
 * the hub between the fingers. Smooth and whole (no tears: it is glass, not smoke).
 */
const panel = (pos: number[], idx: number[], hub: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3, sag: number, S: number, droop: number, flip: boolean): void => {
  const RN = 2;
  const E: THREE.Vector3[] = [];
  for (let j = 0; j <= S; j++) {
    const u = j / S;
    E.push(a.clone().lerp(b, u).lerp(hub, Math.sin(u * Math.PI) * sag));
  }
  const v = (p: THREE.Vector3): number => {
    pos.push(p.x, p.y, p.z);
    return pos.length / 3 - 1;
  };
  const h = v(hub);
  const rows: number[][] = [];
  for (let i = 1; i <= RN; i++) {
    const k = i / RN;
    rows.push(E.map((e, j) => v(hub.clone().lerp(e, k).add(V(0, -droop * Math.sin(k * Math.PI) * Math.sin((j / S) * Math.PI), 0)))));
  }
  const tri = (x: number, y: number, z: number): void => {
    if (flip) idx.push(x, z, y);
    else idx.push(x, y, z);
  };
  for (let j = 0; j < S; j++) tri(h, rows[0]![j]!, rows[0]![j + 1]!);
  for (let i = 1; i < RN; i++) {
    for (let j = 0; j < S; j++) {
      const a0 = rows[i - 1]![j]!, a1 = rows[i - 1]![j + 1]!, b0 = rows[i]![j]!, b1 = rows[i]![j + 1]!;
      tri(a0, b0, b1);
      tri(a0, b1, a1);
    }
  }
};

export const wyrmling = (o: BaseOpts = {}): Model => {
  const c = begin(o, 0.62, 61);
  const { g, rng, R } = c;
  g.position.y = WYRMLING_HOVER;

  // The parent's glass (the same cached materials as models/dragon.ts): black, faceted, glossy; a bluer glass
  // for the plates that catch the light; near-black for belly and jaw; dark glass for teeth and claws.
  const glass = std('#121018', { roughness: 0.14, metalness: 0.25, flatShading: true, bumpScale: 0 });
  const plate = std('#2c2838', { roughness: 0.22, metalness: 0.35, flatShading: true, bumpScale: 0 });
  const dark = std('#08070b', { roughness: 0.3, metalness: 0.2, flatShading: true, bumpScale: 0 });
  const teeth = std('#2a2430', { roughness: 0.15, metalness: 0.5, flatShading: true, bumpScale: 0 });
  const ember = emis('#ff8a6e', '#ff4f3a', 1.7);
  // The membranes: dark glass you can half see through, a red-violet sheen in it.
  const skin = smoke('#2a1224', 0.72, { side: THREE.DoubleSide, emissive: '#3a0a1e', roughness: 0.2, metalness: 0.3 });

  /** A faceted lump of glass. */
  const shard = (parent: THREE.Object3D, mat: THREE.Material, r: number, p: THREE.Vector3, sx = 1, sy = 1, sz = 1, detail = 1): THREE.Mesh => {
    const m = M(new THREE.IcosahedronGeometry(r, detail), mat, parent);
    m.position.copy(p);
    m.scale.set(sx, sy, sz);
    m.rotation.set(R() * 0.6, R() * 0.6, R() * 0.6);
    return m;
  };
  /** A blade of glass from `at` along `dir` (a plate, a spine, a claw), flattened sideways by `flat`. */
  const blade = (parent: THREE.Object3D, mat: THREE.Material, at: THREE.Vector3, dir: THREE.Vector3, len: number, w: number, flat = 0.45, seg = 4): THREE.Mesh => {
    const d = dir.clone().normalize();
    const m = M(new THREE.ConeGeometry(w, len, seg), mat, parent);
    m.position.copy(at).addScaledVector(d, len * 0.45);
    m.quaternion.setFromUnitVectors(V(0, 1, 0), d);
    m.scale.z = flat;
    return m;
  };
  /** A jagged seam of ember: doubled, jittered points joined by a thin glowing tube. */
  const crack = (parent: THREE.Object3D, pts: THREE.Vector3[], r = 0.011, glow = 0): void => {
    const jit: THREE.Vector3[] = [];
    pts.forEach((p, i) => {
      const q = pts[i + 1];
      jit.push(p);
      if (q) jit.push(p.clone().lerp(q, 0.5).add(V(R() * 0.03, R() * 0.03, R() * 0.03)));
    });
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(jit, false, 'catmullrom', 0.05), jit.length - 1, r, 3), ember, parent);
    // The fire inside shows through the split.
    if (glow > 0) glowSprite(parent, '#ff6a1f', glow, jit[Math.floor(jit.length / 2)]!, 0.4);
  };

  /* ---------- bones ---------- */
  const bodyJ = joint(g, V(0, 0, 0), 'body');
  const NECK = [V(0, 0.08, 0.34), V(0, 0.19, 0.54), V(0, 0.3, 0.68), V(0, 0.37, 0.84)];
  const neckC = new THREE.CatmullRomCurve3(NECK);
  const n1 = joint(bodyJ.abs, NECK[0]!, 'neck1');
  const n2 = joint(n1.abs, neckC.getPoint(0.36), 'neck2');
  const n3 = joint(n2.abs, neckC.getPoint(0.7), 'neck3');
  const HEAD = V(0, 0.39, 0.9);
  const headJ = joint(n3.abs, HEAD, 'head');
  // The head is built at the parent's proportions and blown up about its pivot: too big for the body yet.
  const headK = G(headJ.abs, HEAD.x, HEAD.y, HEAD.z);
  headK.scale.setScalar(HEAD_K);
  const head = G(headK, -HEAD.x, -HEAD.y, -HEAD.z);
  const jawJ = joint(head, V(0, 0.35, 0.96), 'jaw');
  const throatJ = joint(n3.abs, V(0, 0.27, 0.78), 'throat');
  const breathJ = joint(head, V(0, 0.36, 1.37), 'breath');
  const body = bodyJ.abs;

  /* ---------- trunk: lean, deep-chested, narrowing to the hips ---------- */
  shard(body, glass, 0.2, V(0, 0.03, 0.2), 1.0, 0.95, 1.3);
  shard(body, glass, 0.16, V(0, 0.0, -0.1), 0.95, 0.9, 1.45);
  shard(body, glass, 0.14, V(0, 0.0, -0.38), 1.05, 0.88, 1.25, 0);
  shard(body, dark, 0.13, V(0, -0.09, 0.04), 0.9, 0.6, 2.0, 0);
  // Shoulders where the wings grow, plates of the bluer glass over them.
  for (const k of [-1, 1]) shard(body, plate, 0.09, V(k * 0.13, 0.11, 0.22), 1.1, 0.8, 1.3, 0);
  // The heart, small as yet, glimpsed through a rift in the chest ringed with blades, like the parent's.
  M(new THREE.IcosahedronGeometry(0.055, 0), ember, body).position.set(0, 0.0, 0.43);
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 0.3;
    blade(body, plate, V(Math.cos(a) * 0.06, Math.sin(a) * 0.06, 0.41), V(Math.cos(a), Math.sin(a), 0.6), 0.09, 0.025);
  }
  const heartGlow = glowSprite(body, '#ff6a1f', 0.42, V(0, 0.0, 0.5), 0.6, true);
  // The parent's violet, faint, deep in the glass.
  glowSprite(body, '#9b4dff', 0.9, V(0, 0.05, 0.0), 0.14);
  // Spine: a ridge of glass blades, tallest over the shoulders, every third one the bluer glass.
  for (let i = 0; i < 7; i++) {
    const u = i / 6;
    const z = 0.34 - u * 0.78;
    blade(body, i % 3 === 1 ? plate : glass, V(0, 0.19 - u * 0.05 - Math.abs(u - 0.2) * 0.04, z), V(0, 1, -0.6), 0.17 - u * 0.07, 0.035);
  }
  // Plates along the flanks, catching the light.
  for (let i = 0; i < 10; i++) {
    const k = i % 2 ? 1 : -1;
    const u = Math.floor(i / 2) / 4;
    const z = 0.28 - u * 0.66;
    blade(body, plate, V(k * (0.16 - u * 0.03), 0.05 + R() * 0.04, z), V(k, 0.6 + R() * 0.3, -0.6), 0.14 + rng() * 0.05, 0.045);
  }
  // Seams of ember over chest, flanks and haunches.
  crack(body, [V(-0.14, 0.1, 0.33), V(-0.06, 0.04, 0.4), V(0.05, 0.07, 0.4), V(0.15, 0.01, 0.32)], 0.012);
  crack(body, [V(0.0, -0.13, 0.3), V(0.02, -0.15, 0.12), V(-0.02, -0.15, -0.06), V(0.01, -0.12, -0.22)], 0.01);
  for (const k of [-1, 1]) {
    crack(body, [V(k * 0.19, 0.08, 0.2), V(k * 0.19, 0.0, 0.07), V(k * 0.16, -0.05, -0.08), V(k * 0.15, 0.03, -0.22), V(k * 0.14, -0.02, -0.38)], 0.011, 0.22);
    crack(body, [V(k * 0.12, 0.12, -0.3), V(k * 0.14, 0.04, -0.44), V(k * 0.1, -0.06, -0.5)], 0.009);
  }

  /* ---------- neck ---------- */
  // Slimmer than the head it carries: shards of glass at the vertebrae, a blade over each.
  const NN = 6;
  for (let i = 0; i < NN; i++) {
    const t = i / NN;
    const bone = t < 0.36 ? n1.abs : t < 0.7 ? n2.abs : n3.abs;
    const p = neckC.getPoint(t + 0.04);
    shard(bone, i % 2 ? glass : plate, 0.085 - t * 0.025, p, 1.05, 0.95, 1.2, 0);
    blade(bone, glass, p.clone().add(V(0, 0.07 - t * 0.02, -0.01)), V(0, 1, -0.8), 0.11 - t * 0.03, 0.024);
  }
  limb(n1.abs, NECK[0]!, neckC.getPoint(0.36), 0.085, 0.07, glass, 6);
  limb(n2.abs, neckC.getPoint(0.36), neckC.getPoint(0.7), 0.07, 0.06, glass, 6);
  limb(n3.abs, neckC.getPoint(0.7), NECK[3]!, 0.06, 0.055, glass, 6);
  // The fire runs down the underside of the neck.
  crack(n1.abs, [V(0, 0.0, 0.38), V(0.01, 0.07, 0.5), V(0, 0.13, 0.57)], 0.012);
  crack(n2.abs, [V(0, 0.13, 0.57), V(-0.01, 0.19, 0.66), V(0, 0.22, 0.72)], 0.012);

  /* ---------- the throat: fire behind ribs of glass ---------- */
  const throat = throatJ.abs;
  const core = M(new THREE.SphereGeometry(0.06, 6, 4), ember, throat);
  core.position.set(0, 0.27, 0.79);
  core.scale.set(0.85, 0.85, 1.5);
  for (let i = 0; i < 3; i++) {
    const rib = M(new THREE.TorusGeometry(0.058, 0.014, 3, 5, Math.PI * 1.1), dark, throat);
    rib.position.set(0, 0.275, 0.74 + i * 0.045);
    rib.rotation.set(0, 0, Math.PI * 1.45 + R() * 0.2);
    rib.scale.set(0.95 - Math.abs(i - 1) * 0.12, 0.9, 1);
  }
  const throatGlow = glowSprite(throat, '#ff5a2a', 0.4, V(0, 0.25, 0.8), 0.55, true);

  /* ---------- head: too big for the body yet, long-snouted, the crown not grown ---------- */
  shard(head, glass, 0.12, V(0, 0.41, 0.97), 1.0, 0.78, 1.2);
  shard(head, plate, 0.075, V(0, 0.46, 0.9), 0.95, 0.5, 1.1, 0);
  const snout = M(new THREE.ConeGeometry(0.085, 0.36, 5), glass, head);
  snout.rotation.x = Math.PI / 2 + 0.07;
  snout.scale.set(1, 1, 0.66);
  snout.position.set(0, 0.39, 1.19);
  // A ridge of plates down the snout.
  for (let i = 0; i < 3; i++) blade(head, plate, V(0, 0.44 - i * 0.012, 1.04 + i * 0.08), V(0, 1, -0.5), 0.07 - i * 0.012, 0.022);
  for (const k of [-1, 1]) {
    // Brows of plate over the eyes; ember eyes, set deep.
    blade(head, plate, V(k * 0.065, 0.45, 1.0), V(k * 0.35, 0.45, -1), 0.17, 0.035);
    M(new THREE.SphereGeometry(0.022, 5, 4), ember, head).position.set(k * 0.068, 0.425, 1.05);
    glowSprite(head, '#ffb36a', 0.2, V(k * 0.074, 0.43, 1.065), 1);
    // The fire shows along the lips, and in a seam under the eye down the cheek.
    crack(head, [V(k * 0.07, 0.36, 0.97), V(k * 0.06, 0.355, 1.1), V(k * 0.04, 0.36, 1.24), V(k * 0.025, 0.37, 1.33)], 0.008);
    crack(head, [V(k * 0.085, 0.41, 1.0), V(k * 0.105, 0.39, 0.92), V(k * 0.11, 0.41, 0.84)], 0.007);
    // Horns swept back, short yet; cheek blades flaring back along the jaw.
    const hp = [V(k * 0.06, 0.47, 0.92), V(k * 0.11, 0.52, 0.8), V(k * 0.14, 0.56, 0.68), V(k * 0.13, 0.61, 0.57)];
    for (let i = 0; i < 3; i++) limb(head, hp[i]!, hp[i + 1]!, 0.03 - i * 0.008, 0.022 - i * 0.008, i === 2 ? plate : glass, 4);
    blade(head, plate, V(k * 0.09, 0.38, 0.93), V(k * 0.75, -0.1, -1), 0.17, 0.028);
    blade(head, glass, V(k * 0.08, 0.34, 0.95), V(k * 0.8, -0.4, -1), 0.11, 0.022);
    // Nostrils that smoulder.
    M(new THREE.SphereGeometry(0.011, 4, 3), ember, head).position.set(k * 0.028, 0.405, 1.34);
  }
  // Where the crown will be: a ring of glass nubs over a faint glow.
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    blade(head, i % 2 ? plate : glass, V(Math.cos(a) * 0.05, 0.49, 0.87 + Math.sin(a) * 0.05), V(Math.cos(a) * 0.4, 1, Math.sin(a) * 0.4 - 0.3), 0.07, 0.016);
  }
  const crownGlow = glowSprite(head, '#ff5a1f', 0.22, V(0, 0.53, 0.87), 0.4, true);
  glowSprite(head, '#9b4dff', 0.3, V(0, 0.52, 0.86), 0.22);
  // Upper teeth, and the fire inside the mouth (seen when the jaw drops).
  for (let i = 0; i < 4; i++) for (const k of [-1, 1]) blade(head, teeth, V(k * (0.05 - i * 0.007), 0.355, 1.02 + i * 0.07), V(0, -1, 0.15), 0.045, 0.01, 1, 3);
  const maw = M(new THREE.SphereGeometry(0.055, 6, 4), ember, head);
  maw.position.set(0, 0.35, 1.08);
  maw.scale.set(0.8, 0.35, 2.1);
  const jaw = jawJ.abs;
  const lower = M(new THREE.ConeGeometry(0.06, 0.34, 5), dark, jaw);
  lower.rotation.x = Math.PI / 2 - 0.05;
  lower.scale.set(0.95, 1, 0.5);
  lower.position.set(0, 0.33, 1.13);
  for (let i = 0; i < 3; i++) for (const k of [-1, 1]) blade(jaw, teeth, V(k * (0.04 - i * 0.007), 0.345, 1.04 + i * 0.07), V(0, 1, 0.15), 0.035, 0.009, 1, 3);
  for (const k of [-1, 1]) blade(jaw, plate, V(k * 0.05, 0.32, 1.0), V(k * 0.5, -0.5, -1), 0.1, 0.02);

  /* ---------- the breath: a jet of fire built tiny at the mouth ---------- */
  const breath = breathJ.abs;
  const bp = breathJ.p.position;
  /** A tongue of the jet: a cone from `off` widening along +Z, tilted off the line by (tx, ty). */
  const jet = (len: number, w: number, off: THREE.Vector3, tx: number, ty: number, seg = 5, near = 0.012): void => {
    const f = M(new THREE.CylinderGeometry(near * BREATH_REST, w * BREATH_REST, len * BREATH_REST, seg, 1, true), ember, breath);
    f.rotation.set(-Math.PI / 2 + tx, ty, 0);
    const dir = V(0, 0, 1).applyEuler(new THREE.Euler(tx, ty, 0));
    f.position.set(bp.x + off.x * BREATH_REST, bp.y + off.y * BREATH_REST, bp.z + off.z * BREATH_REST).addScaledVector(dir, (len / 2) * BREATH_REST);
  };
  // A roaring stream: a core that thins out, tongues licking off it all along and splaying wider the further
  // they go, and a ragged burst where it ends.
  jet(1.35, 0.09, V(0, 0, -0.04), 0, 0, 6, 0.035);
  jet(0.95, 0.12, V(0.01, 0.015, 0.02), -0.06, 0.07, 5, 0.03);
  jet(1.0, 0.11, V(-0.015, -0.01, 0.04), 0.06, -0.08, 5, 0.03);
  for (let i = 0; i < 7; i++) {
    const a = i * 2.4;
    const sp = 0.22 + i * 0.05;
    jet(0.36 + (i % 3) * 0.08, 0.06 + i * 0.006, V(Math.cos(a) * 0.03, Math.sin(a) * 0.03, 0.2 + i * 0.15), -Math.sin(a) * sp, Math.cos(a) * sp, 4);
  }
  for (let i = 0; i < 4; i++) {
    const a = 0.6 + i * 1.6;
    jet(0.4 + (i % 2) * 0.1, 0.14, V(Math.cos(a) * 0.04, Math.sin(a) * 0.04, 1.1 + (i % 2) * 0.1), -Math.sin(a) * 0.75, Math.cos(a) * 0.75, 4);
  }

  /* ---------- wings: arm, forearm and a hand of four long fingers, membranes of glass ---------- */
  const skel: Record<string, THREE.Object3D> = {
    body: bodyJ.p, neck1: n1.p, neck2: n2.p, neck3: n3.p, head: headJ.p, jaw: jawJ.p, throat: throatJ.p, breath: breathJ.p,
  };
  const sparks: { s: THREE.Sprite; p: THREE.Vector3; ph: number }[] = [];
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const X = (x: number, y: number, z: number): THREE.Vector3 => V(k * x, y, z);
    const sh = X(0.15, 0.13, 0.22), el = X(0.66, 0.36, 0.02), wr = X(1.16, 0.5, 0.16);
    const armJ = joint(body, sh, `arm${s}`);
    const foreJ = joint(armJ.abs, el, `fore${s}`);
    const handJ = joint(foreJ.abs, wr, `hand${s}`);
    skel[`arm${s}`] = armJ.p;
    skel[`fore${s}`] = foreJ.p;
    skel[`hand${s}`] = handJ.p;
    const arm = armJ.abs, fore = foreJ.abs, hand = handJ.abs;
    // Bones of glass, knuckled with plate; a seam of ember along the arm.
    limb(arm, sh, el, 0.06, 0.042, glass, 6);
    shard(arm, glass, 0.075, X(0.3, 0.2, 0.15), 1.4, 0.85, 1.1, 0);
    shard(fore, plate, 0.05, el, 1, 1, 1, 0);
    limb(fore, el, wr, 0.04, 0.03, glass, 5);
    shard(hand, plate, 0.045, wr, 1.1, 1, 1, 0);
    crack(arm, [X(0.24, 0.2, 0.2), X(0.42, 0.27, 0.12), X(0.62, 0.36, 0.04)], 0.009);
    // The thumb: a hooked claw of plate on the leading edge, a blade of glass over the wrist.
    blade(hand, plate, X(1.17, 0.52, 0.19), X(0.2, 0.35, 1), 0.13, 0.022, 1);
    blade(hand, glass, wr, X(0.2, 0.6, 0.5), 0.14, 0.03);
    // Four long fingers; a vein of ember runs inside each, under the glass.
    const tips = [X(2.02, 0.62, 0.04), X(1.92, 0.38, -0.5), X(1.58, 0.18, -0.92), X(1.08, 0.04, -1.06)];
    for (const [i, tp] of tips.entries()) {
      const knuckle = wr.clone().lerp(tp, 0.45).add(V(0, 0.03, 0.02));
      limb(hand, wr, knuckle, 0.024, 0.017, glass, 3);
      limb(hand, knuckle, tp, 0.017, 0.004, i === 0 ? plate : glass, 3);
      M(new THREE.TubeGeometry(new THREE.LineCurve3(wr.clone().lerp(tp, 0.1), wr.clone().lerp(tp, 0.7)), 2, 0.007, 3), ember, hand);
    }
    // The membranes: between the fingers on the hand, elbow to the last finger on the forearm, and the inner
    // one from the shoulder to the flank on the arm.
    const root = X(0.12, 0.0, -0.52);
    const parts: [THREE.Object3D, THREE.Vector3, THREE.Vector3, THREE.Vector3, number, number, number][] = [
      [hand, wr, tips[0]!, tips[1]!, 0.16, 5, 0.04],
      [hand, wr, tips[1]!, tips[2]!, 0.2, 5, 0.05],
      [hand, wr, tips[2]!, tips[3]!, 0.2, 5, 0.05],
      [fore, el, tips[3]!, root, 0.22, 6, 0.07],
      [arm, sh, el, root, 0.04, 4, 0.03],
    ];
    for (const [bone, hub, a, b, sag, S, droop] of parts) {
      const pos: number[] = [];
      const idx: number[] = [];
      panel(pos, idx, hub, a, b, sag, S, droop, k < 0);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      geo.setIndex(idx);
      geo.computeVertexNormals();
      M(geo, skin, bone);
    }
    // Sparks of ember smoulder at the fingertips and along the trailing edge.
    for (const [i, tp] of tips.entries()) {
      const bone = hand;
      sparks.push({ s: glowSprite(bone, i % 2 ? '#9b4dff' : '#ff5a2a', 0.1, tp, 0.7, true), p: tp.clone(), ph: rng() });
    }
  }

  /* ---------- tail: a long whip ending in a fan of glass ---------- */
  const tailC = new THREE.CatmullRomCurve3([V(0, 0.0, -0.48), V(0.03, -0.05, -0.9), V(0.1, -0.09, -1.3), V(0.03, -0.11, -1.7), V(-0.08, -0.08, -2.08)]);
  const tailJ: { p: THREE.Group; abs: THREE.Group }[] = [];
  for (let j = 0; j < 5; j++) {
    tailJ.push(joint(j ? tailJ[j - 1]!.abs : body, tailC.getPoint(j * 0.19), `tail${j + 1}`));
    skel[`tail${j + 1}`] = tailJ[j]!.p;
  }
  const tailBone = (t: number): THREE.Object3D => tailJ[Math.min(4, Math.floor(t / 0.19 + 1e-6))]!.abs;
  const TN = 10;
  for (let i = 0; i < TN; i++) {
    const t0 = i / TN, t1 = (i + 1) / TN;
    const bone = tailBone(t0 + 0.02);
    const a = tailC.getPoint(t0), b = tailC.getPoint(t1);
    limb(bone, a, b, 0.095 - t0 * 0.075, 0.095 - t1 * 0.075, glass, t0 < 0.5 ? 5 : 4);
    if (i % 2 === 0) shard(bone, i % 4 === 0 ? glass : plate, 0.085 - t0 * 0.065, a, 1.1, 0.9, 1.3, 0);
    if (i % 2 === 1 && t0 < 0.85) blade(bone, i % 4 === 1 ? plate : glass, a.clone().add(V(0, 0.07 - t0 * 0.055, 0)), V(0, 1, -0.8), 0.1 - t0 * 0.06, 0.022);
  }
  for (const [j, t0, k] of [[0, 0.03, 1], [1, 0.25, -1], [2, 0.46, 1]] as const) {
    const r = 0.085 - t0 * 0.07;
    crack(tailJ[j]!.abs, [0, 0.06, 0.12, 0.17].map((d, i) => tailC.getPoint(t0 + d).add(V(k * r * 0.9, r * (0.5 - (i % 2) * 0.6), 0))), 0.008);
  }
  // The tip: the parent's fan of glass blades, small, and a coal of heat at its root.
  const tip = tailJ[4]!.abs;
  const tp = tailC.getPoint(1);
  for (let i = -1; i <= 1; i++) blade(tip, plate, tp, V(i * 0.6, 0.15, -1), 0.24 - Math.abs(i) * 0.06, 0.045);
  M(new THREE.SphereGeometry(0.022, 5, 4), ember, tip).position.copy(tp).add(V(0, 0.015, 0.01));
  glowSprite(tip, '#ff4f3a', 0.16, tp.clone().add(V(0, 0.02, 0)), 0.6);

  /* ---------- four legs, drawn up under it in flight, claws curled ---------- */
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    for (const fore of [true, false]) {
      const e = fore ? 'F' : 'B';
      const hip = fore ? V(k * 0.12, -0.05, 0.25) : V(k * 0.12, -0.04, -0.36);
      const knee = fore ? V(k * 0.17, -0.2, 0.17) : V(k * 0.17, -0.18, -0.2);
      const ank = fore ? V(k * 0.15, -0.31, 0.3) : V(k * 0.14, -0.3, -0.44);
      const legJ = joint(body, hip, `leg${e}${s}`);
      const shinJ = joint(legJ.abs, knee, `shin${e}${s}`);
      skel[`leg${e}${s}`] = legJ.p;
      skel[`shin${e}${s}`] = shinJ.p;
      shard(legJ.abs, glass, fore ? 0.07 : 0.085, hip.clone().lerp(knee, 0.35), 1, 1.25, 1.1, 0);
      limb(legJ.abs, hip, knee, fore ? 0.05 : 0.06, 0.036, glass, 5);
      limb(shinJ.abs, knee, ank, 0.034, 0.024, glass, 5);
      shard(shinJ.abs, dark, 0.032, ank, 1.1, 0.7, 1.3, 0);
      // Claws of plate, curled under.
      for (let i = 0; i < 3; i++) {
        const a = (i - 1) * 0.45;
        blade(shinJ.abs, plate, ank.clone().add(V(Math.sin(a) * 0.02 * k, -0.01, 0.01)), V(Math.sin(a) * 0.5 * k, -1, fore ? 0.6 : -0.6), 0.08, 0.013, 1, 3);
      }
    }
  }

  // Embers shed off its back, drifting up and back and dying out.
  const embers: { s: THREE.Sprite; x: number; z: number; ph: number }[] = [];
  for (let i = 0; i < 4; i++) embers.push({ s: glowSprite(body, i % 3 ? '#ff6a2a' : '#b06bff', 0.08, V(0, 0, 0), 0.8, true), x: R() * 0.3, z: 0.25 - rng() * 0.6, ph: i / 4 });

  const t = (n: number): void => {
    for (const s of sparks) {
      const q = (s.ph + n * 0.29) % 1;
      s.s.position.set(s.p.x, s.p.y + q * 0.06, s.p.z - q * 0.18);
      s.s.material.opacity = 0.75 * (1 - q) * (q < 0.1 ? q / 0.1 : 1);
    }
    for (const e of embers) {
      const q = (e.ph + n * 0.23) % 1;
      e.s.position.set(e.x + Math.sin(q * 5 + e.ph * 9) * 0.05, 0.18 + q * 0.22, e.z - q * 0.5);
      e.s.material.opacity = 0.85 * (1 - q) * (q < 0.08 ? q / 0.08 : 1);
    }
    throatGlow.material.opacity = 0.5 + 0.15 * Math.sin(n * 2.1) + R() * 0.08;
    heartGlow.material.opacity = 0.5 + 0.15 * Math.sin(n * 1.3 + 1) + R() * 0.06;
    crownGlow.material.opacity = 0.32 + 0.12 * Math.sin(n * 2.7) + R() * 0.08;
  };
  t(0);
  return done(c, WYRMLING_HOVER + 0.6, t, undefined, bonesOf(c, skel));
};
